import { LoopAdminTheme } from '@loopreturns/sonik';

/**
 * Loop Admin (Sonik) dark `semantic.colorScheme.dark.primary` defaults to `{primary.600}` (saturated).
 * MX uses the same pale primary scale as product buttons (`primary.300` base, `200` hover, `400` active).
 *
 * Outlined primary buttons still used `components.button.colorScheme.dark.outlined.primary.borderColor`:
 * `{primary.700}` — that bypasses `semantic.primary.color`, so we align the border to the pale scale.
 *
 * `primitive.bloop` is the source palette for `semantic.primary` in Loop Admin; brand blue **#056EF5**
 * is anchored at `600` (light default) with a full scale for hover, dark UI, and highlights.
 */
const loopBrandBlue = '#056EF5';

/** Full primary scale derived from Loop brand blue (`600` = brand). */
const bloopFromBrand = {
  50: '#eff8ff',
  100: '#dbeffe',
  200: '#a8d4ff',
  300: '#5eb0ff',
  400: '#1a8ef5',
  500: '#0a7ef0',
  600: loopBrandBlue,
  700: '#0456c4',
  800: '#084896',
  900: '#0c3d7a',
  950: '#082848',
} as const;

// Sonik preset is a large nested object; narrow typing here fights `definePreset` output.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- preset merge only
const b = LoopAdminTheme as any;

export const mxLoopAdminPreset = {
  ...b,
  primitive: {
    ...b.primitive,
    bloop: { ...bloopFromBrand },
  },
  semantic: {
    ...b.semantic,
    colorScheme: {
      ...b.semantic.colorScheme,
      dark: {
        ...b.semantic.colorScheme.dark,
        primary: {
          ...b.semantic.colorScheme.dark.primary,
          color: '{primary.300}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.200}',
          activeColor: '{primary.400}',
        },
        highlight: {
          ...b.semantic.colorScheme.dark.highlight,
          background: 'rgba(5, 110, 245, 0.16)',
          focusBackground: 'rgba(5, 110, 245, 0.24)',
        },
      },
    },
  },
  components: {
    ...b.components,
    button: {
      ...b.components.button,
      colorScheme: {
        ...b.components.button.colorScheme,
        dark: {
          ...b.components.button.colorScheme.dark,
          outlined: {
            ...b.components.button.colorScheme.dark.outlined,
            primary: {
              ...b.components.button.colorScheme.dark.outlined.primary,
              borderColor: '{primary.400}',
            },
          },
        },
      },
    },
    /**
     * Quill toolbar strokes use `editor.toolbar.item.*`; Aura maps hover to `{text.color}`, which
     * reads muddy on dark chrome — align with MX pale primary (same family as dark buttons).
     */
    editor: {
      ...b.components.editor,
      colorScheme: {
        ...b.components.editor?.colorScheme,
        dark: {
          ...b.components.editor?.colorScheme?.dark,
          toolbarItem: {
            ...b.components.editor?.colorScheme?.dark?.toolbarItem,
            color: '{surface.400}',
            hoverColor: '{primary.200}',
            activeColor: '{primary.color}',
          },
          overlayOption: {
            ...b.components.editor?.colorScheme?.dark?.overlayOption,
            focusBackground: '{surface.600}',
            focusColor: '{surface.0}',
          },
        },
      },
    },
    // LoopAdmin uses `{surface.800}` for tab hover, which disappears on dark chrome.
    tabs: {
      ...b.components.tabs,
      tab: {
        ...b.components.tabs.tab,
        hoverColor: '{text.color}',
      },
      navButton: {
        ...b.components.tabs.navButton,
        hoverColor: '{text.color}',
      },
    },
  },
};
